import { useWeb3React } from '@web3-react/core';
import { formatUnits } from 'ethers/lib/utils';
import useSWR from 'swr';
import { config } from '../../../config/config';
import { ITradePair__factory } from '../../../config/contracts/typechain';
import useTokenDetails from '../useTokenDetails';
import { verifyChainConfig } from '../../../helpers';
import { EnabledChains } from '../../../config/constants';
import useTradePools from './useTradePools';

type PositionStats = {
    totalLongMargin: string;
    totalLongVolume: string;
    totalLongSize: string;
    totalShortMargin: string;
    totalShortVolume: string;
    totalShortSize: string;
    totalVolume: string;
    totalSizeLimit: string;
};

const usePositionStats = () => {
    const { library, chainId } = useWeb3React();

    const { tradePools } = useTradePools();

    const { decimals, error: tokenDetailsError } = useTokenDetails(
        config.addresses[chainId as EnabledChains[number]]?.collateral
    );

    const { data, error } = useSWR(
        library && decimals && chainId && tradePools
            ? ['positionStats', library, decimals, chainId, tradePools]
            : null,
        async (_, library, decimals, chainId, tradePools) => {
            verifyChainConfig(chainId);

            const positionStats: Record<string, PositionStats> = {};

            await Promise.all(
                tradePools.map(async ({ address }) => {
                    const tradePairContract = ITradePair__factory.connect(
                        address,
                        library
                    );

                    const totalSizeLimit = await tradePairContract.totalVolumeLimit();

                    const stats = await tradePairContract.positionStats();

                    positionStats[address] = {
                        totalLongMargin: formatUnits(stats[0], decimals),
                        totalLongVolume: formatUnits(stats[1], decimals),
                        totalLongSize: formatUnits(stats[2], decimals),
                        totalShortMargin: formatUnits(stats[3], decimals),
                        totalShortVolume: formatUnits(stats[4], decimals),
                        totalShortSize: formatUnits(stats[5], decimals),
                        totalVolume: formatUnits(stats[1].add(stats[4]), decimals),
                        totalSizeLimit: formatUnits(totalSizeLimit, decimals),
                    };
                })
            );

            return positionStats;
        },
        {
            refreshInterval: 2000,
            dedupingInterval: 2000,
        }
    );

    return {
        positionStats: data,
        isLoading: !error && !data && !tokenDetailsError,
        error: error || tokenDetailsError,
    };
};

export default usePositionStats;
